import { Link } from 'react-router-dom';
import { AlertTriangle, Github, Menu, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { useSettings } from '@/hooks/useSettings';
import { UserMenu } from '@/components/auth/UserMenu';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

/**
 * Application header. Surfaces one honest status signal: when the AI provider
 * is not configured, the product says so instead of implying vision analysis ran.
 */
export function Topbar({ onOpenNav }: { onOpenNav: () => void }) {
  const { data: settings } = useSettings();
  const mockedAi = settings?.ai.mocked ?? false;
  const memoryDb = settings?.database.provider === 'memory';

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-bg/85 px-4 backdrop-blur sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenNav}
        aria-label="Open navigation"
      >
        <Menu className="h-4 w-4" />
      </Button>

      <Link to="/dashboard" className="lg:hidden">
        <Logo size="sm" />
      </Link>

      <div className="ml-auto flex items-center gap-2">
        {mockedAi ? (
          <Tooltip content="AI_PROVIDER is not configured. Analyses run on measured CSS only; no vision model is called.">
            <span className="hidden items-center gap-1.5 rounded-full border border-warning/30 bg-warning/10 px-2.5 py-1 text-xs font-medium text-warning sm:inline-flex">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              AI not configured
            </span>
          </Tooltip>
        ) : null}

        {memoryDb ? (
          <Tooltip content="DATABASE_PROVIDER=memory. Analyses are lost when the server restarts.">
            <span className="hidden items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-subtle sm:inline-flex">
              In-memory storage
            </span>
          </Tooltip>
        ) : null}

        <Link
          to="/analyses/new"
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-accent px-3 text-sm font-medium text-accent-fg shadow-xs transition-colors hover:bg-accent-hover"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New Analysis
        </Link>

        <ThemeToggle />

        <UserMenu />

        <Tooltip content="Documentation">
          <a
            href="https://github.com/design-md-generator"
            target="_blank"
            rel="noreferrer noopener"
            className="hidden h-9 w-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-hover hover:text-fg sm:inline-flex"
            aria-label="Open the project repository"
          >
            <Github className="h-4 w-4" />
          </a>
        </Tooltip>
      </div>
    </header>
  );
}
